import { useState, type ReactNode } from "react";
import {
  BadgeCheck,
  Banknote,
  Briefcase,
  CheckCircle2,
  Clock,
  ExternalLink,
  GraduationCap,
  History,
  Linkedin,
  Loader2,
  MapPin,
  UserPlus,
  PenLine,
  Search,
  UserRound,
  Users,
} from "lucide-react";
import type {
  CfoExplainedInput,
  CfoFounderAddition,
  CfoResearchBrief,
  CfoResearchFounder,
  CfoResearchPlace,
  CfoResearchStep,
  CfoScoreExplanation,
} from "@/api/types";
import { InputBlock } from "@/components/companies/CfoScoreCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { countryName } from "@/lib/cfoLabels";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import {
  JOB_DIMENSIONS,
  TEAM_DIMENSIONS,
  foundersSearch,
  needOf,
  founderLinks,
  inputsByDimension,
  investorsLink,
  jobLink,
  jobLinks,
  teamLink,
  type ResearchLink,
} from "./cfoResearch";

/** One block of the research sheet. `need` says what the decision waits for from it
 *  ("Missing: founder place") and rings it amber. */
export function ResearchSection({
  icon: Icon,
  title,
  need,
  aside,
  children,
}: {
  icon: typeof Users;
  title: string;
  need?: string | null;
  aside?: ReactNode;
  children: ReactNode;
}) {
  const missing = Boolean(need);
  return (
    <section
      aria-label={title}
      className={cn(
        "rounded-plane bg-white p-5 shadow-plane",
        missing && "ring-1 ring-inset ring-amber-300",
      )}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <span
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded-lg",
              missing ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500",
            )}
          >
            <Icon className="h-4 w-4" />
          </span>
          {title}
          {need && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
              {need}
            </span>
          )}
        </h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function LinkRow({ links }: { links: ResearchLink[] }) {
  if (!links.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {links.map((link) => (
        <a
          key={link.label}
          href={link.href}
          target="_blank"
          rel="noreferrer noopener"
          title={link.hint}
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 transition-colors hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
        >
          <Search className="h-3 w-3" />
          {link.label}
          <ExternalLink className="h-3 w-3 opacity-50" />
        </a>
      ))}
    </div>
  );
}

function SectionSkeleton() {
  return (
    <div className="space-y-2" aria-hidden>
      <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100" />
      <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
      <div className="h-3 w-3/4 animate-pulse rounded bg-slate-100" />
    </div>
  );
}

const FOUNDER_STATUS: Record<string, { label: string; className: string }> = {
  CONFIRMED: { label: "Confirmed founder", className: "bg-emerald-50 text-emerald-700" },
  CONDITIONAL: { label: "Not confirmed", className: "bg-amber-50 text-amber-800" },
  INSUFFICIENT: { label: "Insufficient evidence", className: "bg-slate-100 text-slate-600" },
  CONTRADICTED: { label: "Contradicted", className: "bg-rose-50 text-rose-700" },
};

const KIND_ICONS: Record<string, typeof MapPin> = {
  education: GraduationCap,
  employment: Briefcase,
  founded_company: Briefcase,
  manual: PenLine,
};

/** `waiting`: the decision waits for the founders, so what blocks is worth pointing at. */
function PlaceItem({ place, waiting }: { place: CfoResearchPlace; waiting: boolean }) {
  const blocks = waiting ? place.blocks : null;
  const Icon = place.source === "Manual answer" ? PenLine : (KIND_ICONS[place.kind] ?? MapPin);
  const meta = [place.item, place.years, place.source].filter(Boolean).join(" · ");
  return (
    <li className={cn("flex items-start gap-2", !place.established && !blocks && "opacity-60")}>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <p className="break-words text-xs text-slate-800">
          {place.place ?? "Place"}
          {place.country ? (
            <span className="font-medium">
              {" "}
              → {countryName(place.country)} ({place.country})
            </span>
          ) : blocks ? (
            // Keeps a US-only career open: the one thing worth finding here.
            <span className="ml-1.5 rounded bg-amber-100 px-1 py-px text-[10px] font-medium uppercase tracking-wide text-amber-800">
              {blocks === "school" ? "School not placed" : "Country not read"}
            </span>
          ) : (
            <span className="ml-1.5 rounded bg-slate-100 px-1 py-px text-[10px] font-medium uppercase tracking-wide text-slate-500">
              No country read
            </span>
          )}
        </p>
        {meta && <p className="break-words text-[11px] text-slate-500">{meta}</p>}
      </div>
      {place.url && (
        <a
          href={place.url}
          target="_blank"
          rel="noreferrer noopener"
          title="Where it was read"
          className="mt-0.5 shrink-0 text-slate-400 hover:text-indigo-600"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </li>
  );
}

function careerSummary(founder: CfoResearchFounder): string | null {
  const career = founder.career;
  if (!career) return null;
  const placed = Object.entries(career.placed)
    .sort((a, b) => b[1] - a[1])
    .map(([code, count]) => `${count} in ${countryName(code)}`);
  const gaps = [
    career.unknown_places ? `${career.unknown_places} place${career.unknown_places > 1 ? "s" : ""} without country` : null,
    career.unplaced_positions ? `${career.unplaced_positions} position${career.unplaced_positions > 1 ? "s" : ""} without place` : null,
    career.unplaced_schools ? `${career.unplaced_schools} school${career.unplaced_schools > 1 ? "s" : ""} not located` : null,
  ].filter(Boolean);
  const read = career.complete ? "Career read in full" : "Career read in part";
  return `${read} (${career.provider}, ${formatDate(career.observed_at)})${
    placed.length ? `: ${placed.join(", ")}` : ": no located item"
  }${gaps.length ? ` · ${gaps.join(" · ")}` : ""}`;
}

/** Places shown before "Show all": a long career stays readable. */
const PLACES_SHOWN = 4;
const PATH_SHOWN = 8;
const HOME = "US";

const STEP_TAGS: Record<CfoResearchStep["standing"], { label: string; className: string } | null> =
  {
    placed: null,
    answered: { label: "Answered", className: "bg-violet-50 text-violet-700" },
    country_not_read: { label: "Country not read", className: "bg-slate-100 text-slate-500" },
    school_not_placed: { label: "School not placed", className: "bg-slate-100 text-slate-500" },
    no_place_given: { label: "No place given", className: "bg-slate-100 text-slate-500" },
    no_place: { label: "No place (remote, online)", className: "bg-slate-100 text-slate-500" },
  };

function PathStep({ step, waiting }: { step: CfoResearchStep; waiting: boolean }) {
  const Icon = KIND_ICONS[step.kind] ?? MapPin;
  const tag =
    waiting && step.blocks
      ? {
          label: step.blocks === "school" ? "School not placed" : "Country not read",
          className: "bg-amber-100 text-amber-800",
        }
      : STEP_TAGS[step.standing];
  const what = [step.title, step.organization].filter(Boolean).join(" at ");
  return (
    <li className="grid grid-cols-[5.75rem_1fr] gap-2 text-xs">
      <span className="pt-px tabular-nums text-slate-500">{step.years ?? "—"}</span>
      <div className="min-w-0">
        <p className="flex items-start gap-1.5 break-words text-slate-800">
          <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span>{what || (step.kind === "education" ? "School" : "Position")}</span>
        </p>
        <p className="break-words pl-5 text-[11px] text-slate-500">
          {step.place}
          {step.countries.length > 0 && (
            <>
              {step.place ? " → " : ""}
              {step.countries.map((code, i) => (
                <span
                  key={code}
                  className={cn(
                    "font-medium",
                    code === HOME ? "text-slate-700" : "text-indigo-700",
                  )}
                >
                  {i > 0 ? ", " : ""}
                  {countryName(code)} ({code})
                </span>
              ))}
            </>
          )}
          {tag && (
            <span
              className={cn(
                "ml-1.5 rounded px-1 py-px text-[10px] font-medium uppercase tracking-wide",
                tag.className,
              )}
            >
              {tag.label}
            </span>
          )}
        </p>
      </div>
    </li>
  );
}

/** A founder's career read in full, in order: positions, current then latest first, then
 *  schools. Owner, 2026-09-28: a career that moves between countries must read in order,
 *  not as places sorted by name. What blocks stays in view when the path is folded. */
function CareerPath({ path, waiting }: { path: CfoResearchStep[]; waiting: boolean }) {
  const [all, setAll] = useState(false);
  const positions = path.filter((step) => step.kind === "employment").length;
  const abroad = [
    ...new Set(path.flatMap((step) => step.countries.filter((code) => code !== HOME))),
  ];
  const beyond = all ? [] : path.slice(PATH_SHOWN).filter((step) => waiting && step.blocks);
  return (
    <>
      <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-slate-500">
        Career path · {positions} position{positions === 1 ? "" : "s"},{" "}
        {path.length - positions} school{path.length - positions === 1 ? "" : "s"}
        {abroad.length > 0 && (
          <span className="normal-case text-indigo-700">
            {" "}
            · outside the US: {abroad.map(countryName).join(", ")}
          </span>
        )}
      </p>
      <ol className="mt-1 space-y-1.5">
        {(all ? path : path.slice(0, PATH_SHOWN)).map((step, index) => (
          <PathStep key={index} step={step} waiting={waiting} />
        ))}
        {beyond.length > 0 && (
          <>
            <li className="pl-[6.25rem] text-[11px] text-slate-400">⋯</li>
            {beyond.map((step, index) => (
              <PathStep key={`beyond-${index}`} step={step} waiting={waiting} />
            ))}
          </>
        )}
      </ol>
      {path.length > PATH_SHOWN && (
        <button
          type="button"
          onClick={() => setAll((value) => !value)}
          className="mt-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800"
        >
          {all ? "Show less" : `Show the whole path (${path.length})`}
        </button>
      )}
    </>
  );
}

/** A place's first year, for an undated-last, latest-first order. */
function firstYear(place: CfoResearchPlace): number {
  return Number.parseInt((place.years ?? "").slice(0, 4), 10) || 0;
}

/** What confirming rests on: a justification and the records linking the person. */
export type ConfirmFounder = (
  founder: CfoResearchFounder,
  reason: string,
  evidenceIds: string[],
) => Promise<boolean>;

const MIN_REASON = 12;

function ConfirmFounderForm({
  founder,
  company,
  onConfirm,
  onCancel,
}: {
  founder: CfoResearchFounder;
  company: string;
  onConfirm: ConfirmFounder;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const [chosen, setChosen] = useState<string[]>(founder.links.map((l) => l.observation_id));
  const [busy, setBusy] = useState(false);
  const short = reason.trim().length < MIN_REASON;
  const id = `cfo-confirm-${founder.id}`;

  async function save() {
    if (short || chosen.length === 0 || busy) return;
    setBusy(true);
    try {
      if (await onConfirm(founder, reason.trim(), chosen)) onCancel();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      aria-label="Confirm a founder"
      className="mt-3 space-y-3 rounded-lg border border-indigo-100 bg-white p-3"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <p className="text-sm font-medium text-slate-900">
        Confirm {founder.name ?? "this person"} as a founder of {company}
      </p>
      <div className="space-y-1.5">
        <label htmlFor={id} className="text-xs font-medium text-slate-700">
          What shows they founded it
        </label>
        <Textarea
          id={id}
          rows={2}
          maxLength={500}
          value={reason}
          disabled={busy}
          placeholder="e.g. “Named co-founder on the company's About page and on their LinkedIn”"
          onChange={(e) => setReason(e.target.value)}
        />
        <p className="text-[11px] text-slate-500">
          Recorded with your name{short ? ` · at least ${MIN_REASON} characters` : ""}.
        </p>
      </div>
      {founder.links.length > 0 && (
        <fieldset className="space-y-1">
          <legend className="text-xs font-medium text-slate-700">Records it confirms</legend>
          {founder.links.map((link) => (
            <label key={link.observation_id} className="flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={chosen.includes(link.observation_id)}
                disabled={busy}
                onChange={(e) =>
                  setChosen((ids) =>
                    e.target.checked
                      ? [...ids, link.observation_id]
                      : ids.filter((value) => value !== link.observation_id),
                  )
                }
              />
              {link.source}
              {link.detail && <span className="text-slate-400">· {link.detail}</span>}
            </label>
          ))}
        </fieldset>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={busy || short || chosen.length === 0}
        >
          {busy ? <Loader2 className="animate-spin" /> : <BadgeCheck />}
          Confirm founder
        </Button>
      </div>
    </form>
  );
}

/** A founder no source listed: resolves to the new founder's id, or null. */
export type AddFounder = (founder: CfoFounderAddition) => Promise<string | null>;

const LINKEDIN_PROFILE = /^https?:\/\/(www\.)?linkedin\.com\/in\/[A-Za-z0-9_-]{1,180}\/?$/i;
const WEB_ADDRESS = /^https?:\/\/[^\s/]+\.[^\s]+$/i;

function AddFounderForm({
  company,
  onAdd,
  onCancel,
}: {
  company: string;
  onAdd: AddFounder;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [source, setSource] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const id = "cfo-add-founder";
  const linkedinValid = !linkedin.trim() || LINKEDIN_PROFILE.test(linkedin.trim());
  const sourceValid = WEB_ADDRESS.test(source.trim());
  const short = reason.trim().length < MIN_REASON;
  const ready = name.trim().length >= 2 && linkedinValid && sourceValid && !short;

  async function save() {
    if (!ready || busy) return;
    setBusy(true);
    try {
      const added = await onAdd({
        name: name.trim(),
        role: role.trim() || null,
        linkedin: linkedin.trim() || null,
        source_url: source.trim(),
        reason: reason.trim(),
      });
      if (added) onCancel();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      aria-label="Add a founder"
      className="mb-3 space-y-3 rounded-lg border border-indigo-100 bg-indigo-50/30 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <p className="text-sm font-medium text-slate-900">Add a founder of {company}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label htmlFor={`${id}-name`} className="text-xs font-medium text-slate-700">
            Name
          </label>
          <Input
            id={`${id}-name`}
            value={name}
            maxLength={200}
            disabled={busy}
            placeholder="First and last name"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-role`} className="text-xs font-medium text-slate-700">
            Role <span className="font-normal text-slate-400">(optional)</span>
          </label>
          <Input
            id={`${id}-role`}
            value={role}
            maxLength={200}
            disabled={busy}
            placeholder="e.g. “Co-Founder & CEO”"
            onChange={(e) => setRole(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-linkedin`} className="text-xs font-medium text-slate-700">
            LinkedIn profile <span className="font-normal text-slate-400">(recommended)</span>
          </label>
          <Input
            id={`${id}-linkedin`}
            type="url"
            value={linkedin}
            maxLength={255}
            disabled={busy}
            placeholder="https://www.linkedin.com/in/…"
            aria-invalid={!linkedinValid}
            onChange={(e) => setLinkedin(e.target.value)}
          />
          {!linkedinValid && (
            <p className="text-[11px] text-rose-600">A profile address: https://www.linkedin.com/in/…</p>
          )}
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-source`} className="text-xs font-medium text-slate-700">
            Where you read they founded it
          </label>
          <Input
            id={`${id}-source`}
            type="url"
            value={source}
            maxLength={500}
            disabled={busy}
            placeholder="https://… (the company's About page, a press release…)"
            aria-invalid={source.trim() !== "" && !sourceValid}
            onChange={(e) => setSource(e.target.value)}
          />
        </div>
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-reason`} className="text-xs font-medium text-slate-700">
          Justification
        </label>
        <Textarea
          id={`${id}-reason`}
          rows={2}
          maxLength={500}
          value={reason}
          disabled={busy}
          placeholder="e.g. “Listed as co-founder on the company's About page and on Crunchbase”"
          onChange={(e) => setReason(e.target.value)}
        />
        <p className="text-[11px] text-slate-500">
          The founder is confirmed at once, under your name
          {short ? ` · at least ${MIN_REASON} characters` : ""}. Their places can then be
          documented.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" size="sm" disabled={!ready || busy}>
          {busy ? <Loader2 className="animate-spin" /> : <UserPlus />}
          Add founder
        </Button>
      </div>
    </form>
  );
}

function FounderRow({
  founder,
  company,
  waiting,
  onDocument,
  onConfirm,
}: {
  founder: CfoResearchFounder;
  company: string;
  /** The decision waits for the founders. */
  waiting: boolean;
  onDocument?: (founder: CfoResearchFounder) => void;
  onConfirm?: ConfirmFounder;
}) {
  const [all, setAll] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const status = FOUNDER_STATUS[founder.status] ?? {
    label: founder.status.toLowerCase(),
    className: "bg-slate-100 text-slate-600",
  };
  const name = founder.name ?? "Unnamed person";
  const path = founder.path ?? [];
  // With a full career read, its places are in the path; the others (a reviewer's
  // documented place, another provider's record) follow it. Latest first either way.
  const places = founder.places
    .filter((p) => !(path.length > 0 && p.in_path))
    .sort((a, b) => Number(!a.blocks) - Number(!b.blocks) || firstYear(b) - firstYear(a));
  const counted = places.filter((p) => p.established).length;
  const summary = careerSummary(founder);
  return (
    <li className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-slate-500 ring-1 ring-slate-200">
            <UserRound className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-slate-900">
              {name}
              <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-medium", status.className)}>
                {status.label}
              </span>
              {!founder.founder && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                  Not listed as founder
                </span>
              )}
              {founder.human_reviewed && (
                <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700">
                  {founder.links.some((l) => l.url) ? "Added by a reviewer" : "Reviewed by a person"}
                </span>
              )}
              {founder.links
                .filter((l) => l.url)
                .map((l) => (
                  <a
                    key={l.observation_id}
                    href={l.url ?? undefined}
                    target="_blank"
                    rel="noreferrer noopener"
                    title="Where the reviewer read it"
                    className="inline-flex items-center gap-0.5 text-[11px] font-normal text-slate-500 hover:text-indigo-600"
                  >
                    source
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ))}
            </p>
            {founder.roles.length > 0 && (
              <p className="text-xs text-slate-500">{founder.roles.join(" · ")}</p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {founder.linkedin && (
            <a
              href={founder.linkedin}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 rounded-md bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700 hover:bg-sky-100"
            >
              <Linkedin className="h-3.5 w-3.5" />
              Profile
            </a>
          )}
          {onDocument && (
            <button
              type="button"
              onClick={() => onDocument(founder)}
              className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700"
            >
              <MapPin className="h-3.5 w-3.5" />
              Document a place
            </button>
          )}
          {onConfirm && founder.status !== "CONFIRMED" && !confirming && (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-white px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
            >
              <BadgeCheck className="h-3.5 w-3.5" />
              Confirm founder
            </button>
          )}
        </div>
      </div>
      {confirming && onConfirm && (
        <ConfirmFounderForm
          founder={founder}
          company={company}
          onConfirm={onConfirm}
          onCancel={() => setConfirming(false)}
        />
      )}
      <div className="mt-2 pl-10">
        <LinkRow links={founder.name ? founderLinks(founder.name, company) : []} />
        {path.length > 0 && <CareerPath path={path} waiting={waiting} />}
        {places.length > 0 ? (
          <>
            <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-slate-500">
              {path.length > 0 ? "Other documented places" : "Documented places"} · {counted}{" "}
              with a country
            </p>
            <ul className="mt-1 space-y-1.5">
              {(all ? places : places.slice(0, PLACES_SHOWN)).map((place, index) => (
                <PlaceItem key={`${place.place}-${index}`} place={place} waiting={waiting} />
              ))}
            </ul>
            {places.length > PLACES_SHOWN && (
              <button
                type="button"
                onClick={() => setAll((value) => !value)}
                className="mt-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800"
              >
                {all ? "Show fewer" : `Show all ${places.length} places`}
              </button>
            )}
          </>
        ) : (
          path.length === 0 && (
            <p className="mt-2 text-xs text-slate-500">No place documented for this person.</p>
          )
        )}
        {summary && <p className="mt-2 text-[11px] text-slate-500">{summary}</p>}
      </div>
    </li>
  );
}

export function FoundersSection({
  brief,
  need,
  detail,
  onDocument,
  onConfirm,
  onAdd,
}: {
  brief: CfoResearchBrief | null;
  /** What the decision waits for from the founders, if anything. */
  need?: string | null;
  /** The same, said in full: whom to confirm, whose career is unread, what is unplaced. */
  detail?: string | null;
  /** Offered for confirmed founders when a place can be documented. */
  onDocument?: (founder: CfoResearchFounder) => void;
  /** Offered for the others, to anyone who may review founders. */
  onConfirm?: ConfirmFounder;
  /** A founder no source listed, to anyone who may review founders. */
  onAdd?: AddFounder;
}) {
  const [adding, setAdding] = useState(false);
  return (
    <ResearchSection
      icon={Users}
      title="Founders"
      need={need}
      aside={
        brief ? (
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <LinkRow links={[foundersSearch(brief.name)]} />
            {onAdd && !adding && (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700"
              >
                <UserPlus className="h-3.5 w-3.5" />
                Add a founder
              </button>
            )}
          </div>
        ) : null
      }
    >
      {adding && brief && onAdd && (
        <AddFounderForm company={brief.name} onAdd={onAdd} onCancel={() => setAdding(false)} />
      )}
      {!brief ? (
        <SectionSkeleton />
      ) : brief.founders.length === 0 ? (
        <p className="text-sm text-slate-500">
          {onAdd
            ? "No founder identified yet. Find them with the search above, then add them here: their places can then be documented."
            : "No founder identified yet. Find them, then note who they are with “Can’t find” so the next person starts from there."}
        </p>
      ) : (
        <>
          {need && (detail || !brief.founders.some((f) => f.status === "CONFIRMED")) && (
            <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {!brief.founders.some((f) => f.status === "CONFIRMED") && (
                <>
                  Only a confirmed founder&rsquo;s place counts, and none is confirmed yet.{" "}
                  {onConfirm
                    ? "Confirm one below once you have checked they founded the company, or add the founder you found. "
                    : "Confirm one from the company page’s founder evidence. "}
                </>
              )}
              {detail}
            </p>
          )}
          <ul className="space-y-2">
            {brief.founders.map((founder) => (
              <FounderRow
                key={founder.id}
                founder={founder}
                company={brief.name}
                waiting={Boolean(need)}
                onDocument={
                  onDocument && founder.status === "CONFIRMED" ? onDocument : undefined
                }
                onConfirm={onConfirm}
              />
            ))}
          </ul>
        </>
      )}
    </ResearchSection>
  );
}

function usd(amount: number | null | undefined): string | null {
  if (!amount || amount <= 0) return null;
  for (const [size, suffix] of [
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "K"],
  ] as const) {
    if (amount >= size) return `$${(amount / size).toFixed(1).replace(/\.0$/, "")}${suffix}`;
  }
  return `$${Math.round(amount)}`;
}

export function FundingSection({ brief, need }: { brief: CfoResearchBrief | null; need?: string | null }) {
  const excluded = new Set((brief?.excluded_incubators ?? []).map((i) => i.toLowerCase()));
  const flagged = (name: string) =>
    [...excluded].some((incubator) => name.toLowerCase().includes(incubator));
  return (
    <ResearchSection
      icon={Banknote}
      title="Funding & investors"
      need={need}
      aside={brief ? <LinkRow links={[investorsLink(brief.name)]} /> : null}
    >
      {!brief ? (
        <SectionSkeleton />
      ) : (
        <div className="space-y-3">
          {brief.rounds.length > 0 ? (
            <ul className="space-y-1.5">
              {brief.rounds.map((round, index) => (
                <li key={`${round.announced_on}-${index}`} className="flex items-center justify-between gap-2 text-sm">
                  <span className="min-w-0 truncate text-slate-800">
                    <span className="font-medium">{round.type ?? round.stage ?? "Round"}</span>
                    <span className="text-slate-500"> · {formatDate(round.announced_on)}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="tabular-nums text-slate-700">{usd(round.amount_usd) ?? "—"}</span>
                    {round.url && (
                      <a
                        href={round.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        title="The round on Crunchbase"
                        className="text-slate-400 hover:text-indigo-600"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">No funding round recorded.</p>
          )}
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Investors{brief.investors_complete === false ? " · partial list" : ""}
            </p>
            {brief.investors.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {brief.investors.map((investor) => (
                  <span
                    key={investor}
                    className={cn(
                      "rounded-md px-1.5 py-0.5 text-xs",
                      flagged(investor)
                        ? "bg-rose-50 font-medium text-rose-700 ring-1 ring-inset ring-rose-200"
                        : "bg-slate-100 text-slate-700",
                    )}
                  >
                    {investor}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-xs text-slate-500">No investor list in the sources.</p>
            )}
          </div>
          {brief.excluded_incubators.length > 0 && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <span className="font-medium text-slate-700">Excluded incubators:</span>{" "}
              {brief.excluded_incubators.join(", ")}. Check the full investor and accelerator
              list on Crunchbase or the company&rsquo;s site.
            </p>
          )}
          {brief.categories.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {brief.categories.map((category) => (
                <span key={category} className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-700">
                  {category}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </ResearchSection>
  );
}

function DimensionBlocks({
  dimensions,
  inputs,
  missing,
  link,
}: {
  dimensions: string[];
  inputs: Record<string, CfoExplainedInput>;
  missing: string[];
  link: (dimension: string) => ResearchLink | null;
}) {
  return (
    <div className="space-y-2">
      {dimensions.map((dimension) => {
        const input = inputs[dimension];
        const extra = link(dimension);
        return (
          <div
            key={dimension}
            className={cn(missing.includes(dimension) && "rounded-lg ring-1 ring-amber-300")}
          >
            {input ? (
              <InputBlock input={input} />
            ) : (
              <p className="rounded-lg border border-slate-100 bg-slate-50/70 p-3 text-xs text-slate-500">
                Not evaluated for this company.
              </p>
            )}
            {extra && (
              <div className="mt-1.5">
                <LinkRow links={[extra]} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function TeamSection({
  company,
  explanation,
  loaded,
  missing,
}: {
  company: string;
  explanation: CfoScoreExplanation | null;
  loaded: boolean;
  missing: string[];
}) {
  const inputs = inputsByDimension(explanation);
  return (
    <ResearchSection
      icon={Users}
      title="Team in place"
      need={needOf(TEAM_DIMENSIONS, missing)}
    >
      {!loaded ? (
        <SectionSkeleton />
      ) : (
        <DimensionBlocks
          dimensions={TEAM_DIMENSIONS}
          inputs={inputs}
          missing={missing}
          link={(dimension) => teamLink(dimension, company)}
        />
      )}
    </ResearchSection>
  );
}

export function HiringSection({
  company,
  domain,
  explanation,
  loaded,
  missing,
}: {
  company: string;
  domain?: string | null;
  explanation: CfoScoreExplanation | null;
  loaded: boolean;
  missing: string[];
}) {
  const inputs = inputsByDimension(explanation);
  return (
    <ResearchSection
      icon={Briefcase}
      title="Job postings"
      need={needOf(JOB_DIMENSIONS, missing)}
      aside={<LinkRow links={jobLinks(company, domain).slice(0, 1)} />}
    >
      {!loaded ? (
        <SectionSkeleton />
      ) : (
        <div className="space-y-3">
          <LinkRow links={jobLinks(company, domain).slice(1)} />
          <DimensionBlocks
            dimensions={JOB_DIMENSIONS}
            inputs={inputs}
            missing={missing}
            link={(dimension) => jobLink(dimension, company)}
          />
        </div>
      )}
    </ResearchSection>
  );
}

export function HistorySection({ brief }: { brief: CfoResearchBrief | null }) {
  if (!brief || brief.history.length === 0) return null;
  return (
    <ResearchSection icon={History} title="Previous research">
      <ul className="space-y-2">
        {brief.history.map((note, index) => (
          <li key={`${note.at}-${index}`} className="flex items-start gap-2 text-xs">
            {note.result === "not_found" ? (
              <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            ) : (
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
            )}
            <div className="min-w-0">
              <p className="text-slate-800">
                {note.result === "not_found"
                  ? "Nothing found"
                  : `${note.answers} answer${note.answers === 1 ? "" : "s"}`}
                <span className="text-slate-500">
                  {" "}
                  · {note.user_name ?? "someone"} · {formatDate(note.at)}
                </span>
              </p>
              {note.note && <p className="whitespace-pre-wrap text-slate-600">{note.note}</p>}
            </div>
          </li>
        ))}
      </ul>
    </ResearchSection>
  );
}
